import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DocumentUploadPanel } from '../components/DocumentUploadPanel';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Ship, 
    Plus, 
    MapPin, 
    Gauge,
    Edit,
    Navigation,
    Users,
    FileText,
    Trash2,
    Eye,
    Anchor,
    Upload,
    Download,
    History,
    Clock,
    AlertCircle,
    LogIn,
    LogOut,
    Search,
    X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const tipoLabels = {
    empurrador: 'Empurrador',
    balsa: 'Balsa',
    propulsado: 'Propulsado'
};

const statusLabels = {
    navegando: 'Navegando',
    atracado: 'Atracado',
    manutencao: 'Manutenção',
    emergencia: 'Emergência'
};

const tipoDocumentoLabels = {
    registro: 'Registro',
    certificado: 'Certificado',
    licenca: 'Licença',
    vistoria: 'Vistoria',
    seguro: 'Seguro',
    outros: 'Outros'
};

const ConvoysPage = () => {
    const [convoys, setConvoys] = useState([]);
    const [tripulantes, setTripulantes] = useState([]);
    const [documentos, setDocumentos] = useState([]);
    const [historicoTripulantes, setHistoricoTripulantes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [tripulanteDialogOpen, setTripulanteDialogOpen] = useState(false);
    const [documentoDialogOpen, setDocumentoDialogOpen] = useState(false);
    const [editingConvoy, setEditingConvoy] = useState(null);
    const [selectedConvoy, setSelectedConvoy] = useState(null);
    const [editingTripulante, setEditingTripulante] = useState(null);
    const [editingDocumento, setEditingDocumento] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const { hasRole } = useAuth();

    const [formData, setFormData] = useState({
        nome: '',
        codigo: '',
        tipo: 'empurrador',
        rota_atual: '',
        posicao_lat: '',
        posicao_lon: '',
        velocidade: '',
        status: 'atracado',
        observacoes: ''
    });

    const [tripulanteForm, setTripulanteForm] = useState({
        nome: '',
        funcao: '',
        cir: '',
        embarcacao_id: '',
        ativo: true
    });

    const [documentoForm, setDocumentoForm] = useState({
        nome: '',
        tipo: 'registro',
        numero: '',
        embarcacao_id: '',
        data_emissao: '',
        data_validade: '',
        observacoes: ''
    });

    const fileInputRef = useRef(null);
    const [uploadingDoc, setUploadingDoc] = useState(null);

    useEffect(() => {
        fetchConvoys();
    }, []);

    const fetchConvoys = async () => {
        try {
            const response = await axios.get(`${API}/convoys`);
            setConvoys(response.data);
        } catch (error) {
            toast.error('Erro ao carregar embarcações');
        } finally {
            setLoading(false);
        }
    };

    const fetchTripulantes = async (embarcacaoId) => {
        try {
            const response = await axios.get(`${API}/tripulantes?embarcacao_id=${embarcacaoId}`);
            setTripulantes(response.data);
        } catch (error) {
            console.error('Erro ao carregar tripulantes:', error);
        }
    };

    const fetchHistoricoTripulantes = async (embarcacaoId) => {
        try {
            const response = await axios.get(`${API}/historico-tripulantes?embarcacao_id=${embarcacaoId}`);
            setHistoricoTripulantes(response.data);
        } catch (error) {
            console.error('Erro ao carregar histórico:', error);
        }
    };

    const fetchDocumentos = async (embarcacaoId) => {
        try {
            const response = await axios.get(`${API}/documentos-embarcacao?embarcacao_id=${embarcacaoId}`);
            setDocumentos(response.data);
        } catch (error) {
            console.error('Erro ao carregar documentos:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const payload = {
            ...formData,
            posicao_lat: formData.posicao_lat ? parseFloat(formData.posicao_lat) : null,
            posicao_lon: formData.posicao_lon ? parseFloat(formData.posicao_lon) : null,
            velocidade: formData.velocidade ? parseFloat(formData.velocidade) : null
        };

        try {
            if (editingConvoy) {
                await axios.put(`${API}/convoys/${editingConvoy.id}`, payload);
                toast.success('Embarcação atualizada!');
            } else {
                await axios.post(`${API}/convoys`, payload);
                toast.success('Embarcação cadastrada!');
            }
            
            setDialogOpen(false);
            resetForm();
            fetchConvoys();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar embarcação');
        }
    };

    const handleTripulanteSubmit = async (e) => {
        e.preventDefault();
        
        const payload = {
            ...tripulanteForm,
            embarcacao_id: selectedConvoy.id
        };

        try {
            if (editingTripulante) {
                await axios.put(`${API}/tripulantes/${editingTripulante.id}`, payload);
                toast.success('Tripulante atualizado!');
            } else {
                await axios.post(`${API}/tripulantes`, payload);
                toast.success('Tripulante cadastrado!');
            }
            
            setTripulanteDialogOpen(false);
            resetTripulanteForm();
            fetchTripulantes(selectedConvoy.id);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar tripulante');
        }
    };

    const handleDocumentoSubmit = async (e) => {
        e.preventDefault();
        
        const payload = {
            ...documentoForm,
            embarcacao_id: selectedConvoy.id
        };

        try {
            if (editingDocumento) {
                await axios.put(`${API}/documentos-embarcacao/${editingDocumento.id}`, payload);
                toast.success('Documento atualizado!');
            } else {
                await axios.post(`${API}/documentos-embarcacao`, payload);
                toast.success('Documento cadastrado!');
            }
            
            setDocumentoDialogOpen(false);
            resetDocumentoForm();
            fetchDocumentos(selectedConvoy.id);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar documento');
        }
    };

    const deleteTripulante = async (tripulanteId) => {
        if (!window.confirm('Deseja remover este tripulante?')) return;
        try {
            await axios.delete(`${API}/tripulantes/${tripulanteId}`);
            toast.success('Tripulante removido!');
            fetchTripulantes(selectedConvoy.id);
        } catch (error) {
            toast.error('Erro ao remover tripulante');
        }
    };

    const deleteDocumento = async (documentoId) => {
        if (!window.confirm('Deseja remover este documento?')) return;
        try {
            await axios.delete(`${API}/documentos-embarcacao/${documentoId}`);
            toast.success('Documento removido!');
            fetchDocumentos(selectedConvoy.id);
        } catch (error) {
            toast.error('Erro ao remover documento');
        }
    };

    const handleFileUpload = async (documentoId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            setUploadingDoc(documentoId);
            await axios.post(`${API}/documentos-embarcacao/${documentoId}/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Arquivo enviado!');
            fetchDocumentos(selectedConvoy.id);
        } catch (error) {
            toast.error('Erro ao enviar arquivo');
        } finally {
            setUploadingDoc(null);
        }
    };

    const getDocumentoStatus = (dataValidade) => {
        if (!dataValidade) return null;
        const hoje = new Date();
        const validade = new Date(dataValidade);
        const diasRestantes = Math.ceil((validade - hoje) / (1000 * 60 * 60 * 24));
        
        if (diasRestantes < 0) return { status: 'vencido', dias: diasRestantes, color: 'text-red-400' };
        if (diasRestantes <= 7) return { status: 'critico', dias: diasRestantes, color: 'text-red-400' };
        if (diasRestantes <= 30) return { status: 'alerta', dias: diasRestantes, color: 'text-amber-400' };
        return { status: 'ok', dias: diasRestantes, color: 'text-emerald-400' };
    };

    const resetForm = () => {
        setFormData({
            nome: '',
            codigo: '',
            tipo: 'empurrador',
            rota_atual: '',
            posicao_lat: '',
            posicao_lon: '',
            velocidade: '',
            status: 'atracado',
            observacoes: ''
        });
        setEditingConvoy(null);
    };

    const resetTripulanteForm = () => {
        setTripulanteForm({
            nome: '',
            funcao: '',
            cir: '',
            embarcacao_id: '',
            ativo: true
        });
        setEditingTripulante(null);
    };

    const resetDocumentoForm = () => {
        setDocumentoForm({
            nome: '',
            tipo: 'registro',
            numero: '',
            embarcacao_id: '',
            data_emissao: '',
            data_validade: '',
            observacoes: ''
        });
        setEditingDocumento(null);
    };

    const openEditDialog = (convoy) => {
        setEditingConvoy(convoy);
        setFormData({
            nome: convoy.nome,
            codigo: convoy.codigo,
            tipo: convoy.tipo || 'empurrador',
            rota_atual: convoy.rota_atual || '',
            posicao_lat: convoy.posicao_lat?.toString() || '',
            posicao_lon: convoy.posicao_lon?.toString() || '',
            velocidade: convoy.velocidade?.toString() || '',
            status: convoy.status,
            observacoes: convoy.observacoes || ''
        });
        setDialogOpen(true);
    };

    const openDetailDialog = async (convoy) => {
        setSelectedConvoy(convoy);
        await Promise.all([
            fetchTripulantes(convoy.id),
            fetchDocumentos(convoy.id),
            fetchHistoricoTripulantes(convoy.id)
        ]);
        setDetailDialogOpen(true);
    };

    const openEditTripulante = (tripulante) => {
        setEditingTripulante(tripulante);
        setTripulanteForm({
            nome: tripulante.nome,
            funcao: tripulante.funcao,
            cir: tripulante.cir,
            embarcacao_id: tripulante.embarcacao_id,
            ativo: tripulante.ativo
        });
        setTripulanteDialogOpen(true);
    };

    const openEditDocumento = (documento) => {
        setEditingDocumento(documento);
        setDocumentoForm({
            nome: documento.nome,
            tipo: documento.tipo,
            numero: documento.numero || '',
            embarcacao_id: documento.embarcacao_id,
            data_emissao: documento.data_emissao || '',
            data_validade: documento.data_validade || '',
            observacoes: documento.observacoes || ''
        });
        setDocumentoDialogOpen(true);
    };

    const canEdit = hasRole(['admin', 'gestor', 'operador']);
    const canDelete = hasRole(['admin']);

    // Filtrar convoys com base na busca
    const filteredConvoys = convoys.filter(convoy => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            convoy.nome?.toLowerCase().includes(term) ||
            convoy.codigo?.toLowerCase().includes(term) ||
            tipoLabels[convoy.tipo]?.toLowerCase().includes(term) ||
            statusLabels[convoy.status]?.toLowerCase().includes(term) ||
            convoy.rota_atual?.toLowerCase().includes(term)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="convoys-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Embarcações
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Cadastro e monitoramento da frota
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                    {/* Campo de Busca */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar embarcações..."
                            className="pl-10 pr-8 w-full sm:w-64 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                            data-testid="search-convoys"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                data-testid="clear-search-convoys"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                
                    {canEdit && (
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <Button 
                                    data-testid="btn-new-convoy"
                                    className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Nova Embarcação
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="font-heading text-xl text-white">
                                        {editingConvoy ? 'Editar Embarcação' : 'Nova Embarcação'}
                                    </DialogTitle>
                                </DialogHeader>
                            
                                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Nome
                                        </Label>
                                        <Input
                                            data-testid="input-convoy-nome"
                                            value={formData.nome}
                                            onChange={(e) => setFormData({...formData, nome: e.target.value})}
                                            required
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Código
                                        </Label>
                                        <Input
                                            data-testid="input-convoy-codigo"
                                            value={formData.codigo}
                                            onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                                            required
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Tipo de Embarcação
                                        </Label>
                                        <Select 
                                            value={formData.tipo} 
                                            onValueChange={(value) => setFormData({...formData, tipo: value})}
                                        >
                                            <SelectTrigger data-testid="select-convoy-tipo" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {Object.entries(tipoLabels).map(([key, label]) => (
                                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Status
                                        </Label>
                                        <Select 
                                            value={formData.status} 
                                            onValueChange={(value) => setFormData({...formData, status: value})}
                                        >
                                            <SelectTrigger data-testid="select-convoy-status" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {Object.entries(statusLabels).map(([key, label]) => (
                                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Rota Atual
                                    </Label>
                                    <Input
                                        data-testid="input-convoy-rota"
                                        value={formData.rota_atual}
                                        onChange={(e) => setFormData({...formData, rota_atual: e.target.value})}
                                        placeholder="Ex: Porto A → Porto B"
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                    />
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Latitude
                                        </Label>
                                        <Input
                                            type="number"
                                            step="any"
                                            value={formData.posicao_lat}
                                            onChange={(e) => setFormData({...formData, posicao_lat: e.target.value})}
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Longitude
                                        </Label>
                                        <Input
                                            type="number"
                                            step="any"
                                            value={formData.posicao_lon}
                                            onChange={(e) => setFormData({...formData, posicao_lon: e.target.value})}
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Velocidade (nós)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            value={formData.velocidade}
                                            onChange={(e) => setFormData({...formData, velocidade: e.target.value})}
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Observações
                                    </Label>
                                    <Textarea
                                        value={formData.observacoes}
                                        onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[80px]"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-convoy"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        {editingConvoy ? 'Atualizar' : 'Cadastrar'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
                </div>
            </div>

            {/* Convoys Grid */}
            {filteredConvoys.length === 0 ? (
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="p-12 text-center">
                        <Ship className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                        <p className="text-slate-400">
                            {searchTerm ? 'Nenhuma embarcação encontrada para a busca' : 'Nenhuma embarcação cadastrada'}
                        </p>
                        {canEdit && !searchTerm && (
                            <p className="text-sm text-slate-500 mt-2">
                                Clique em "Nova Embarcação" para começar
                            </p>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredConvoys.map((convoy) => (
                        <Card 
                            key={convoy.id} 
                            data-testid={`convoy-card-${convoy.id}`}
                            className="bg-slate-900/50 border-slate-800 rounded-sm card-hover"
                        >
                            <CardHeader className="border-b border-slate-800 p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`status-indicator status-${convoy.status}`} />
                                        <div>
                                            <CardTitle className="font-heading text-lg text-white">
                                                {convoy.nome}
                                            </CardTitle>
                                            <p className="text-xs text-slate-500 font-mono">{convoy.codigo}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => openDetailDialog(convoy)}
                                            className="text-slate-400 hover:text-white hover:bg-slate-800"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </Button>
                                        {canEdit && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => openEditDialog(convoy)}
                                                className="text-slate-400 hover:text-white hover:bg-slate-800"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-slate-800 text-slate-300 border border-slate-700">
                                        {tipoLabels[convoy.tipo] || 'N/A'}
                                    </span>
                                    <span className={`
                                        px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                        ${convoy.status === 'navegando' ? 'badge-success' : ''}
                                        ${convoy.status === 'atracado' ? 'badge-info' : ''}
                                        ${convoy.status === 'manutencao' ? 'badge-warning' : ''}
                                        ${convoy.status === 'emergencia' ? 'badge-danger' : ''}
                                    `}>
                                        {statusLabels[convoy.status]}
                                    </span>
                                </div>

                                {convoy.velocidade && (
                                    <div className="flex items-center gap-2 text-slate-400">
                                        <Gauge className="w-3 h-3" />
                                        <span className="text-xs font-mono">{convoy.velocidade} nós</span>
                                    </div>
                                )}

                                {convoy.rota_atual && (
                                    <div className="flex items-center gap-2 text-slate-400">
                                        <Navigation className="w-3 h-3" />
                                        <span className="text-xs">{convoy.rota_atual}</span>
                                    </div>
                                )}

                                {(convoy.posicao_lat && convoy.posicao_lon) && (
                                    <div className="flex items-center gap-2 text-slate-500">
                                        <MapPin className="w-3 h-3" />
                                        <span className="text-xs font-mono">
                                            {convoy.posicao_lat?.toFixed(4)}, {convoy.posicao_lon?.toFixed(4)}
                                        </span>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Detail Dialog with Tabs */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <Anchor className="w-5 h-5 text-cyan-400" />
                            {selectedConvoy?.nome} - {tipoLabels[selectedConvoy?.tipo]}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedConvoy && (
                        <Tabs defaultValue="info" className="mt-4">
                            <TabsList className="bg-slate-800/50 border border-slate-700 grid grid-cols-4">
                                <TabsTrigger value="info" className="data-[state=active]:bg-cyan-600">
                                    <Ship className="w-4 h-4 mr-2" />
                                    Info
                                </TabsTrigger>
                                <TabsTrigger value="tripulantes" className="data-[state=active]:bg-cyan-600">
                                    <Users className="w-4 h-4 mr-2" />
                                    Tripulantes
                                </TabsTrigger>
                                <TabsTrigger value="historico" className="data-[state=active]:bg-cyan-600">
                                    <History className="w-4 h-4 mr-2" />
                                    Histórico
                                </TabsTrigger>
                                <TabsTrigger value="documentos" className="data-[state=active]:bg-cyan-600">
                                    <FileText className="w-4 h-4 mr-2" />
                                    Documentos
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="info" className="mt-4 space-y-4">
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">Código</p>
                                        <p className="text-white mt-1 font-mono">{selectedConvoy.codigo}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">Tipo</p>
                                        <p className="text-white mt-1">{tipoLabels[selectedConvoy.tipo]}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">Status</p>
                                        <span className={`
                                            inline-block mt-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                            ${selectedConvoy.status === 'navegando' ? 'badge-success' : ''}
                                            ${selectedConvoy.status === 'atracado' ? 'badge-info' : ''}
                                            ${selectedConvoy.status === 'manutencao' ? 'badge-warning' : ''}
                                            ${selectedConvoy.status === 'emergencia' ? 'badge-danger' : ''}
                                        `}>
                                            {statusLabels[selectedConvoy.status]}
                                        </span>
                                    </div>
                                </div>
                                {selectedConvoy.rota_atual && (
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">Rota Atual</p>
                                        <p className="text-white mt-1">{selectedConvoy.rota_atual}</p>
                                    </div>
                                )}
                                {selectedConvoy.observacoes && (
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">Observações</p>
                                        <p className="text-slate-300 mt-1 text-sm">{selectedConvoy.observacoes}</p>
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="tripulantes" className="mt-4">
                                <div className="flex justify-between items-center mb-4">
                                    <p className="text-sm text-slate-400">Tripulantes cadastrados nesta embarcação</p>
                                    {canEdit && (
                                        <Button
                                            onClick={() => {
                                                resetTripulanteForm();
                                                setTripulanteDialogOpen(true);
                                            }}
                                            className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm text-xs"
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Adicionar Tripulante
                                        </Button>
                                    )}
                                </div>
                                
                                {tripulantes.length === 0 ? (
                                    <div className="text-center py-8 text-slate-500">
                                        Nenhum tripulante cadastrado
                                    </div>
                                ) : (
                                    <Table className="table-naval">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Nome</TableHead>
                                                <TableHead>Função</TableHead>
                                                <TableHead>CIR</TableHead>
                                                <TableHead>Status</TableHead>
                                                {canEdit && <TableHead>Ações</TableHead>}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {tripulantes.map((trip) => (
                                                <TableRow key={trip.id}>
                                                    <TableCell className="text-white">{trip.nome}</TableCell>
                                                    <TableCell className="text-slate-300">{trip.funcao}</TableCell>
                                                    <TableCell className="font-mono text-cyan-400">{trip.cir}</TableCell>
                                                    <TableCell>
                                                        <span className={trip.ativo ? 'badge-success' : 'badge-danger'}>
                                                            {trip.ativo ? 'Ativo' : 'Inativo'}
                                                        </span>
                                                    </TableCell>
                                                    {canEdit && (
                                                        <TableCell>
                                                            <div className="flex gap-1">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => openEditTripulante(trip)}
                                                                    className="text-slate-400 hover:text-white"
                                                                >
                                                                    <Edit className="w-4 h-4" />
                                                                </Button>
                                                                {canDelete && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => deleteTripulante(trip.id)}
                                                                        className="text-red-400 hover:text-red-300"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </TabsContent>

                            {/* Aba de Histórico de Tripulantes */}
                            <TabsContent value="historico" className="mt-4">
                                <div className="mb-4">
                                    <p className="text-sm text-slate-400">Histórico de embarques e desembarques</p>
                                </div>
                                
                                {historicoTripulantes.length === 0 ? (
                                    <div className="text-center py-8 text-slate-500">
                                        <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        Nenhum histórico registrado
                                    </div>
                                ) : (
                                    <div className="space-y-3 max-h-96 overflow-y-auto">
                                        {historicoTripulantes.map((hist) => (
                                            <div 
                                                key={hist.id}
                                                className="p-3 bg-slate-800/30 rounded-sm border-l-2 border-cyan-500/50"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-white font-medium">{hist.tripulante_nome}</p>
                                                        <p className="text-xs text-slate-400">{hist.funcao}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="flex items-center gap-1 text-emerald-400 text-xs">
                                                            <LogIn className="w-3 h-3" />
                                                            <span>{hist.data_embarque}</span>
                                                        </div>
                                                        {hist.data_desembarque && (
                                                            <div className="flex items-center gap-1 text-red-400 text-xs mt-1">
                                                                <LogOut className="w-3 h-3" />
                                                                <span>{hist.data_desembarque}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {hist.motivo_desembarque && (
                                                    <p className="text-xs text-slate-500 mt-2 border-t border-slate-700 pt-2">
                                                        Motivo: {hist.motivo_desembarque}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="documentos" className="mt-4">
                                <div className="flex justify-between items-center mb-4">
                                    <p className="text-sm text-slate-400">Documentos da embarcação</p>
                                    {canEdit && (
                                        <Button
                                            onClick={() => {
                                                resetDocumentoForm();
                                                setDocumentoDialogOpen(true);
                                            }}
                                            className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm text-xs"
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Adicionar Documento
                                        </Button>
                                    )}
                                </div>

                                {/* Upload de Arquivos - Laudos, Certificados, Vistorias */}
                                <DocumentUploadPanel modulo="embarcacoes" entidadeId={selectedConvoy?.id} entidadeNome={selectedConvoy?.nome} />
                                
                                {documentos.length === 0 ? (
                                    <div className="text-center py-8 text-slate-500">
                                        Nenhum documento cadastrado
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {documentos.map((doc) => {
                                            const status = getDocumentoStatus(doc.data_validade);
                                            return (
                                                <div 
                                                    key={doc.id}
                                                    className={`p-3 rounded-sm border ${
                                                        status?.status === 'vencido' ? 'bg-red-950/30 border-red-900/50' :
                                                        status?.status === 'critico' ? 'bg-amber-950/30 border-amber-900/50' :
                                                        status?.status === 'alerta' ? 'bg-amber-950/20 border-amber-900/30' :
                                                        'bg-slate-800/30 border-slate-700'
                                                    }`}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2">
                                                                <p className="text-white font-medium">{doc.nome}</p>
                                                                <span className="px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm bg-slate-700 text-slate-300">
                                                                    {tipoDocumentoLabels[doc.tipo]}
                                                                </span>
                                                            </div>
                                                            {doc.numero && (
                                                                <p className="text-xs text-cyan-400 font-mono mt-1">Nº {doc.numero}</p>
                                                            )}
                                                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                                                                {doc.data_emissao && (
                                                                    <span>Emissão: {doc.data_emissao}</span>
                                                                )}
                                                                {doc.data_validade && (
                                                                    <span className={status?.color}>
                                                                        Validade: {doc.data_validade}
                                                                        {status && (
                                                                            <span className="ml-1">
                                                                                ({status.dias < 0 ? `vencido há ${Math.abs(status.dias)} dias` : `${status.dias} dias`})
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {doc.arquivo_nome && (
                                                                <div className="flex items-center gap-2 mt-2">
                                                                    <FileText className="w-3 h-3 text-slate-400" />
                                                                    <a 
                                                                        href={`${window.location.origin}${doc.arquivo_url}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-xs text-cyan-400 hover:text-cyan-300 flex-1 truncate"
                                                                    >
                                                                        {doc.arquivo_nome}
                                                                    </a>
                                                                    <a
                                                                        href={`${window.location.origin}${doc.arquivo_url}${doc.arquivo_url?.includes('?') ? '&' : '?'}download=${encodeURIComponent(doc.arquivo_nome)}`}
                                                                        className="p-1 hover:bg-slate-700 rounded"
                                                                        title="Baixar"
                                                                        data-testid={`btn-download-doc-emb-${doc.id}`}
                                                                    >
                                                                        <Download className="w-3 h-3 text-slate-400 hover:text-cyan-300" />
                                                                    </a>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {canEdit && (
                                                            <div className="flex items-center gap-1">
                                                                <input
                                                                    type="file"
                                                                    ref={fileInputRef}
                                                                    className="hidden"
                                                                    onChange={(e) => {
                                                                        if (e.target.files[0]) {
                                                                            handleFileUpload(doc.id, e.target.files[0]);
                                                                        }
                                                                    }}
                                                                />
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setUploadingDoc(doc.id);
                                                                        fileInputRef.current?.click();
                                                                    }}
                                                                    disabled={uploadingDoc === doc.id}
                                                                    className="text-slate-400 hover:text-white"
                                                                    title="Upload arquivo"
                                                                >
                                                                    <Upload className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => openEditDocumento(doc)}
                                                                    className="text-slate-400 hover:text-white"
                                                                >
                                                                    <Edit className="w-4 h-4" />
                                                                </Button>
                                                                {canDelete && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => deleteDocumento(doc.id)}
                                                                        className="text-red-400 hover:text-red-300"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </TabsContent>
                        </Tabs>
                    )}
                </DialogContent>
            </Dialog>

            {/* Tripulante Dialog */}
            <Dialog open={tripulanteDialogOpen} onOpenChange={(open) => {
                setTripulanteDialogOpen(open);
                if (!open) resetTripulanteForm();
            }}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white">
                            {editingTripulante ? 'Editar Tripulante' : 'Novo Tripulante'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleTripulanteSubmit} className="space-y-4 mt-4">
                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Nome Completo
                            </Label>
                            <Input
                                data-testid="input-tripulante-nome"
                                value={tripulanteForm.nome}
                                onChange={(e) => setTripulanteForm({...tripulanteForm, nome: e.target.value})}
                                required
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                    Função
                                </Label>
                                <Input
                                    data-testid="input-tripulante-funcao"
                                    value={tripulanteForm.funcao}
                                    onChange={(e) => setTripulanteForm({...tripulanteForm, funcao: e.target.value})}
                                    required
                                    placeholder="Ex: Comandante, Maquinista..."
                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                    Número da CIR
                                </Label>
                                <Input
                                    data-testid="input-tripulante-cir"
                                    value={tripulanteForm.cir}
                                    onChange={(e) => setTripulanteForm({...tripulanteForm, cir: e.target.value})}
                                    required
                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setTripulanteDialogOpen(false)}
                                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                data-testid="btn-save-tripulante"
                                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                {editingTripulante ? 'Atualizar' : 'Cadastrar'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Documento Dialog */}
            <Dialog open={documentoDialogOpen} onOpenChange={(open) => {
                setDocumentoDialogOpen(open);
                if (!open) resetDocumentoForm();
            }}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white">
                            {editingDocumento ? 'Editar Documento' : 'Novo Documento'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleDocumentoSubmit} className="space-y-4 mt-4">
                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Nome do Documento
                            </Label>
                            <Input
                                data-testid="input-documento-nome"
                                value={documentoForm.nome}
                                onChange={(e) => setDocumentoForm({...documentoForm, nome: e.target.value})}
                                required
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                    Tipo
                                </Label>
                                <Select 
                                    value={documentoForm.tipo} 
                                    onValueChange={(value) => setDocumentoForm({...documentoForm, tipo: value})}
                                >
                                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-slate-900 border-slate-800">
                                        {Object.entries(tipoDocumentoLabels).map(([key, label]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                    Número
                                </Label>
                                <Input
                                    value={documentoForm.numero}
                                    onChange={(e) => setDocumentoForm({...documentoForm, numero: e.target.value})}
                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                    Data de Emissão
                                </Label>
                                <Input
                                    type="date"
                                    value={documentoForm.data_emissao}
                                    onChange={(e) => setDocumentoForm({...documentoForm, data_emissao: e.target.value})}
                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                    Data de Validade
                                </Label>
                                <Input
                                    type="date"
                                    value={documentoForm.data_validade}
                                    onChange={(e) => setDocumentoForm({...documentoForm, data_validade: e.target.value})}
                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Observações
                            </Label>
                            <Textarea
                                value={documentoForm.observacoes}
                                onChange={(e) => setDocumentoForm({...documentoForm, observacoes: e.target.value})}
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                            />
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setDocumentoDialogOpen(false)}
                                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                data-testid="btn-save-documento"
                                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                {editingDocumento ? 'Atualizar' : 'Cadastrar'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ConvoysPage;
